<template>
  <b-container class="h-100">
    <transition name="slide-fade">
      <b-row class="h-100">
        <b-col
          class="d-flex flex-column justify-content-center login-wrapper--right-form"
        >
          <div class="d-flex">
            <b-img src="/img/logo.svg" fluid />
          </div>

          <div class="mt-4">
            <h3 class="font-weight-bold">Send Reset Code</h3>
            <p class="mt-3">
              Enter code sent to: <br />
              <shimmer v-if="loading" height="20px" width="40%"></shimmer>
              <b v-else>{{ displayAccount }}</b>
            </p>
          </div>

          <b-form @submit.prevent="onSubmit">
            <b-form-group id="input-group-2" class="col-12">
              <label class="font-weight-bold">Reset Code</label>

              <b-form-input
                id="input-2"
                v-model="form.token"
                class="form-control effect-26"
                :class="form.token ? 'has-content' : ''"
                type="text"
                required
                autofocus
                placeholder="Enter reset code"
              ></b-form-input>
            </b-form-group>
            <div class="input-error" v-if="form.$getError('token')">
              {{ form.$getError('token')[0] }}
            </div>

            <b-form-group id="input-group-3" class="col-12">
              <label class="font-weight-bold">Password</label>

              <b-form-input
                id="input-3"
                v-model="form.password"
                class="form-control effect-26"
                :class="form.password ? 'has-content' : ''"
                type="password"
                autocomplete="new-password"
                required
                placeholder="Enter new password"
              ></b-form-input>
            </b-form-group>
            <div class="input-error" v-if="form.$getError('password')">
              {{ form.$getError('password')[0] }}
            </div>

            <b-form-group id="input-group-4" class="col-12">
              <label class="font-weight-bold">Confirm Password</label>

              <b-form-input
                id="input-4"
                v-model="form.password_confirmation"
                class="effect-26"
                :class="form.password_confirmation ? 'has-content' : ''"
                type="password"
                autocomplete="new-password"
                required
                placeholder="Enter password confirmation"
              ></b-form-input>
            </b-form-group>
            <password-match-hint
              :password="form.password"
              :confirmation="form.password_confirmation"
            />
            <div
              class="input-error"
              v-if="form.$getError('password_confirmation')"
            >
              {{ form.$getError('password_confirmation')[0] }}
            </div>

            <div class="ta-right mt-4">
              <b-button
                class="form-button full-width"
                type="submit"
                variant="primary"
                :disabled="form.$busy"
              >
                <b-spinner
                  small
                  class="mr-2"
                  v-if="form.$busy"
                  label="Loading"
                />
                <span>SUBMIT</span>
              </b-button>
            </div>

            <div class="ta-center mt-4">
              <b-link
                class="form-link"
                :disabled="resending || form.$busy || cooldown > 0"
                @click.prevent="onResendCode"
                >Didn't receive a code?
                <span v-if="cooldown > 0">({{ cooldown }}s)</span></b-link
              >
              <div v-if="resendNotice" class="text-success small mt-1">
                {{ resendNotice }}
              </div>
            </div>
          </b-form>
        </b-col>
      </b-row>
    </transition>
  </b-container>
</template>

<script>
import Form from '@/utils/form'
import { formatMaskedPhone } from '@/utils/phonePolicy'
import shimmer from '@/components/shimmers/shimmer'
import PasswordMatchHint from '@/components/forms/PasswordMatchHint.vue'

export default {
  components: {
    shimmer,
    PasswordMatchHint,
  },
  data() {
    return {
      loading: false,
      verifiedAccount: '',
      resending: false,
      cooldown: 0,
      cooldownTimer: null,
      resendNotice: null,
      form: new Form({
        email: '',
        token: '',
        password: '',
        password_confirmation: '',
      }),
    }
  },

  beforeDestroy() {
    if (this.cooldownTimer) {
      clearInterval(this.cooldownTimer)
      this.cooldownTimer = null
    }
  },

  computed: {
    // The API masks a phone number; an email passes through untouched.
    displayAccount() {
      return formatMaskedPhone(this.verifiedAccount)
    },
  },

  methods: {
    // Mirrors VerifyForm.vue's resend cooldown so "didn't receive a code"
    // behaves the same way here as it does on the email/phone verify screens
    // — a visible 30s countdown, not just a disabled link with no feedback.
    startCooldown(seconds = 30) {
      this.cooldown = seconds
      if (this.cooldownTimer) {
        clearInterval(this.cooldownTimer)
      }
      this.cooldownTimer = setInterval(() => {
        if (this.cooldown <= 1) {
          clearInterval(this.cooldownTimer)
          this.cooldownTimer = null
          this.cooldown = 0
        } else {
          this.cooldown -= 1
        }
      }, 1000)
    },

    async onSubmit(evt) {
      this.form.$busy = true
      await this.$api
        .post('auth/reset-password', {
          username: this.form.email,
          token: this.form.token,
          password: this.form.password,
          password_confirmation: this.form.password_confirmation,
        })
        .then(({ data }) => {
          this.form.$busy = false
          sessionStorage.removeItem('resetPasswordEmail')
          this.$router.replace({ name: 'auth.login-page' })
        })
        .catch((error) => {
          this.form.$busy = false
          this.form.$handleError(error)
        })
    },
    async onResendCode(evt) {
      if (this.resending || this.form.$busy || this.cooldown > 0) return

      this.resending = true
      this.resendNotice = null
      await this.$api
        .post('auth/forgot-password', {
          username: this.form.email,
        })
        .then(({ data }) => {
          this.resendNotice = 'A new code has been sent.'
          this.startCooldown()
        })
        .catch((error) => {
          this.form.$handleError(error)
        })
        .finally(() => {
          this.resending = false
        })
    },
    async getVerifiedAccount() {
      // Reached directly rather than through the forgot-password step, so
      // there is no email to look up. Skip the call instead of posting
      // undefined and leaving the shimmer running on the error.
      if (!this.form.email) return

      this.loading = true
      await this.$api
        .post('auth/reset-password/get-verified-account', {
          email: this.form.email,
        })
        .then(({ data }) => {
          this.verifiedAccount = data.verified_account
        })
        .catch(() => {
          // The address is only shown as confirmation of where the code
          // went; failing to resolve it should not strand the form behind
          // a shimmer the user cannot clear.
          this.verifiedAccount = this.form.email
        })
        .finally(() => {
          this.loading = false
        })
    },
  },

  created() {
    // Route params aren't part of the URL for this route, so a page reload
    // loses them — fall back to the copy ForgotPasswordPage stashed in
    // sessionStorage before navigating here.
    this.form.email =
      this.$route.params.email ||
      sessionStorage.getItem('resetPasswordEmail') ||
      ''
    this.getVerifiedAccount()
  },

  watch: {
    'form.token'() {
      this.form.$clearError('token')
    },
    'form.password'() {
      this.form.$clearError('password')
    },
  },
}
</script>
